/* eslint-disable no-console */
import { logger } from '@upflowy/logger';
import { FirebaseError } from 'firebase/app';
import { FirestoreError } from 'firebase/firestore';
import {
  createContext,
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import styled from 'styled-components';

import { NavigatorNetworkError } from '../helpers/fetch-documents';
import { useNetworkState } from '../hooks/useNetworkState';

export const DEFAULT_MAX_RETRY_COUNT = 3; // 3 retries
export const DEFAULT_RETRY_DELAY_MS = 3000; // 3 seconds
export const LAST_OFFLINE_THRESHOLD_MS = 5000; // 5 seconds

export type OfflineCallback = () => void | Promise<void>;

const AlertContainer = styled.div`
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;

  position: absolute;
  z-index: 9999;
  right: 0;
  bottom: 0;

  display: flex;
  display: block;

  max-width: 400px;
  margin: 10px;

  color: #333;
  border-left: 6px solid var(--dark-background-color, #6048f3);
  border-radius: 4px;
  background-color: #fff;
  box-shadow: rgba(0, 0, 0, 0%) 0 0 0 0, rgba(0, 0, 0, 0%) 0 0 0 0,
    rgba(0, 0, 0, 10%) 0 4px 6px -1px, rgba(0, 0, 0, 10%) 0 2px 4px -2px;

  align-items: center;
  justify-content: center;

  overflow: hidden;
  transition: all 300ms ease-in-out;

  p {
    margin: 0;
    padding: 10px 15px;
  }
`;

interface NetworkErrorHandlerProviderProps {
  maxRetryCount?: number;
  retryDelayMs?: number;
}

interface NetworkErrorHandlerContext {
  retryCount: number;
  networkErrorHandler: (e: Error, isOfflineCallback: OfflineCallback) => void;
}

export const NetworkErrorContext = createContext<NetworkErrorHandlerContext>({
  retryCount: 0,
  networkErrorHandler: () => null,
});

export const useNetworkErrorHandler = () => useContext(NetworkErrorContext);

export const NetworkErrorHandlerProvider: FunctionComponent<NetworkErrorHandlerProviderProps> = ({
  maxRetryCount = DEFAULT_MAX_RETRY_COUNT,
  retryDelayMs = DEFAULT_RETRY_DELAY_MS,
  children,
}) => {
  const [retryCount, setRetryCount] = useState(0);
  const { isOnline } = useNetworkState();
  const [showAlert, setShowAlert] = useState(!isOnline);

  const defaultMessage =
    // eslint-disable-next-line max-len
    "It looks like your connection might be a bit slow or spotty at the moment! We're still fetching your experience but it might take a bit longer than usual.";

  const [message, setMessage] = useState<string>(defaultMessage);
  // I want to make it easier to tell that we're dealing with a cleanup handler here
  // so I'm disabling the eslint rule for this.
  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      logger.setContext('NetworkErrorHandlerProvider', {});
    };
  }, []);

  // Once we're back online, reset the retry count
  useEffect(() => {
    setRetryCount(0);
    setShowAlert(!isOnline);
    setMessage(defaultMessage);
  }, [isOnline]);

  const networkErrorHandler = useCallback(
    (error: Error, isOfflineCallback: OfflineCallback) => {
      const isFirestoreNetworkError =
        error instanceof FirestoreError && error.code === 'unavailable';
      const isFirebaseNetworkError = error instanceof FirebaseError && error.code === 'unavailable';
      const isNavigatorNetworkError = error instanceof NavigatorNetworkError;

      if (isFirestoreNetworkError || isFirebaseNetworkError || isNavigatorNetworkError) {
        if (retryCount <= maxRetryCount) {
          setTimeout(() => {
            setMessage(
              'It looks like you might not be online, hang tight as we continue attempting to load your experience!',
            );
            setRetryCount(retryCount + 1);
            console.warn('==================================================');
            console.warn(
              'connection issues detected, retrying:',
              retryCount,
              'at: ',
              new Date().toUTCString(),
            );
            console.warn('==================================================');
          }, retryDelayMs);
        } else if (isOfflineCallback) {
          setMessage('Uh oh! It looks like you might not be online, please try again later!');
          isOfflineCallback();
          setShowAlert(!isOnline && window.location.pathname !== '/offline');
        }
      } else {
        throw error;
      }
    },
    [isOnline, maxRetryCount, retryCount, retryDelayMs],
  );

  logger.setContext('NetworkErrorHandler Provider', {
    props: { maxRetryCount, retryDelayMs },
    state: { retryCount },
  });

  return (
    <NetworkErrorContext.Provider
      value={{
        networkErrorHandler,
        retryCount,
      }}
    >
      <AlertContainer
        data-testid="network-alert-container"
        style={{ height: showAlert ? 'auto' : '0px' }}
      >
        <p>{message}</p>
      </AlertContainer>

      {children}
    </NetworkErrorContext.Provider>
  );
};
